import { Button, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import { KeyedMutator } from 'swr/_internal';
import { AccountData, AccountService, UpdateBusinessDetailsInput } from '../../../api';
import { formDataToPolicy, PolicyEditor, policyToFormData } from './PolicyEditor';

type PoliciesTabProps = {
  account: AccountData;
  updateLocalAccount: KeyedMutator<AccountData>;
  setIsUpdatedSnackOpen: (value: boolean) => void;
};

export const PoliciesTab = ({ account, updateLocalAccount, setIsUpdatedSnackOpen }: PoliciesTabProps) => {
  const languages = account.customer_facing_widgets_languages;
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);

  const initialValues = {
    terms: policyToFormData(account?.business_details?.terms_policy, languages),
    privacy: policyToFormData(account?.business_details?.privacy_policy, languages),
    cancellation: policyToFormData(account?.business_details?.cancellation_policy, languages),
  };

  const onSubmit = async (values: typeof initialValues) => {
    setIsUpdating(true);
    const input: UpdateBusinessDetailsInput = {
      terms_policy: formDataToPolicy(values.terms),
      privacy_policy: formDataToPolicy(values.privacy),
      cancellation_policy: formDataToPolicy(values.cancellation),
    };
    const updatedAccount = await AccountService.updateBusinessDetails(input);
    await updateLocalAccount(updatedAccount);
    setIsUpdating(false);
    setIsUpdatedSnackOpen(true);
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={onSubmit}>
      {({ handleSubmit, values, setFieldValue }) => (
        <Form onSubmit={handleSubmit}>
          <Grid container flexDirection="column">
            <Grid container flexDirection="column" flex={1} gap={2}>
              <Grid item>
                <Typography variant="h4">{t('business_details.policies')}</Typography>
              </Grid>
              <Grid item>
                <Typography variant="body1" color="text.secondary">
                  {t('business_details.note')}
                </Typography>
              </Grid>
              <PolicyEditor
                title={t('business_details.terms_policy')}
                value={values.terms}
                onChange={(v) => setFieldValue('terms', v)}
                fieldNamePrefix="terms"
                disabled={isUpdating}
              />
              <PolicyEditor
                title={t('business_details.privacy_policy')}
                value={values.privacy}
                onChange={(v) => setFieldValue('privacy', v)}
                fieldNamePrefix="privacy"
                disabled={isUpdating}
              />
              <PolicyEditor
                title={t('business_details.cancellation_policy')}
                value={values.cancellation}
                onChange={(v) => setFieldValue('cancellation', v)}
                fieldNamePrefix="cancellation"
                disabled={isUpdating}
              />
            </Grid>
            <Grid container justifyContent="flex-end" mt={3}>
              <Button type="submit" variant="contained" disabled={isUpdating}>
                {t('save')}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
